/* eslint-disable react/no-array-index-key */
import React, {
  useCallback, useEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import IconMC from './IconMC';
import ButtonMC from './ButtonMC';
import MultiSelectDropdownMC from './MultiSelectDropdownMC';

/**
 * MultiSelectDropdownGroupMC component
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the group
 * @param {Array<IOptionProps>} props.options - Options for all dropdowns
 * @param {Function} props.onChange - Callback when values change (receives array of arrays)
 * @param {Array<Array<string|number>>} [props.defaultValue] - Initial values
 * @param {string} [props.addButtonLabel] - Label for add button
 * @param {string} [props.className] - Additional class names
 * @param {boolean} [props.useFloatingLabel] - Whether to use floating label on the first dropdown
 * @param {string} [props.labelTooltip] - Optional tooltip for the floating label
 *
 * @typedef {Object} IOptionProps
 * @property {string | number} id - Unique identifier
 * @property {string} label - Display text
 */
const MultiSelectDropdownGroupMC = ({
  label,
  options,
  name,
  onChange,
  defaultValue = [[]],
  placeholder = '',
  addButtonLabel = "+",
  className = '',
  search = false,
  useFloatingLabel = false,
  labelTooltip = ''
}) => {
  const { t } = useTranslation();
  const [dropdowns, setDropdowns] = useState(defaultValue);
  const previousDefaultValueRef = useRef(null);

  const isGroupValueEqual = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b || a.length !== b.length) return false;
    return a.every((inner, i) => {
      const other = b[i];
      if (!Array.isArray(other) || inner.length !== other.length) return false;
      return inner.every((val, j) => other[j] === val);
    });
  };

  useEffect(() => {
    const nextValue = Array.isArray(defaultValue) ? defaultValue : [[]];
    const prevValue = previousDefaultValueRef.current;
    if (!isGroupValueEqual(prevValue, nextValue)) {
      previousDefaultValueRef.current = nextValue;
      setDropdowns(nextValue);
    }
  }, [defaultValue]);

  const separator = useCallback(() => t('operator.and'), [t]);

  const handleDropdownChange = useCallback((index, values) => {
    setDropdowns((prev) => {
      const newDropdowns = [...prev];
      newDropdowns[index] = values;
      onChange(name, newDropdowns);
      return newDropdowns;
    });
  }, [name, onChange]);

  const addDropdown = useCallback(() => {
    setDropdowns((prev) => {
      if (prev[prev.length - 1].length === 0) return prev;
      const newDropdowns = [...prev, []];
      onChange(name, newDropdowns);
      return newDropdowns;
    });
  }, [name, onChange]);

  const removeDropdown = useCallback((index) => {
    setDropdowns((prev) => {
      if (prev.length <= 1) return prev;
      const newDropdowns = prev.filter((_, i) => i !== index);
      onChange(name, newDropdowns);
      return newDropdowns;
    });
  }, [name, onChange]);

  const handleSelect = useCallback((nameOrIndex, value) => {
    handleDropdownChange(Number(nameOrIndex), value);
  }, [handleDropdownChange]);

  const isLastDropdownEmpty = dropdowns[dropdowns.length - 1]
    .length === 0;

  return (
    <div className={`tw-wrapper ${className}`}>
      {label && !useFloatingLabel && (
        <h3 className="tw-text-sm tw-font-bold tw-text-gray-700">{label}</h3>
      )}

      {dropdowns.map((values, index) => (
        <div key={index} className="tw-flex tw-flex-col">
          {index > 0 && (
            <div className="tw-relative tw-flex tw-items-center tw-my-2.5">
              <hr className="tw-flex-grow tw-border-t tw-border-gray-200" />
              <span className="tw-px-2 tw-text-gray-700">{t('operator.or')}</span>
              <hr className="tw-flex-grow tw-border-t tw-border-gray-200" />
            </div>
          )}
          <div className="tw-flex tw-gap-2.5">
            <MultiSelectDropdownMC
              id={`${name}-${index}`}
              search={search}
              name={String(index)}
              options={options}
              defaultValue={values}
              onSelect={handleSelect}
              placeholder={placeholder}
              separator={separator}
              useFloatingLabel={useFloatingLabel && index === 0}
              label={index === 0 ? label : undefined}
              labelTooltip={index === 0 ? labelTooltip : undefined}
            />
            <button
              type="button"
              onClick={() => removeDropdown(index)}
              className="tw-p-2 tw-text-gray-700 hover:tw-text-red-500"
            >
              <IconMC name="trash-2-outline" />
            </button>
          </div>
          {index === dropdowns.length - 1 && (
          <ButtonMC
            text={addButtonLabel}
            variant="SECONDARY"
            outlined
            onClick={addDropdown}
            disabled={isLastDropdownEmpty}
            className="tw-mt-6"
            size="medium"
          />
          )}
        </div>
      ))}
    </div>
  );
};

export default MultiSelectDropdownGroupMC;
